import Page from 'components/layout/Page';
import BlogCard from 'components/marketingSite/blog/BlogCard';
import BlogCta from 'components/marketingSite/blog/BlogCta';
import BlogPagination from 'components/marketingSite/blog/BlogPagination';
import Badge from 'components/shared/Badge.styled';
import { Heading1 } from 'components/shared/Typography';
import { useI18n } from 'hooks/useI18n';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import {
  WP_REST_API_Attachment,
  WP_REST_API_Categories,
  WP_REST_API_Post,
  WP_REST_API_Posts
} from 'wp-types';
import {
  StyledBlog,
  StyledBlogCards,
  StyledBlogHeader,
  StyledTags
} from './BlogComponent.styled';

type BlogComponentProps = {
  posts?: WP_REST_API_Posts;
  categories?: WP_REST_API_Categories;
  totalPosts: number;
  totalPages: number;
  currentPage: number;
  category?: string;
};

function getPostThumb(post: WP_REST_API_Post): string | undefined {
  const embedded = post._embedded?.['wp:featuredmedia'];
  const media =
    embedded && embedded[0]
      ? (embedded[0] as WP_REST_API_Attachment)
      : undefined;

  return media ? media.source_url : undefined;
}

const BlogComponent: React.FC<BlogComponentProps> = ({
  posts,
  categories,
  category,
  totalPosts,
  totalPages,
  currentPage
}) => {
  const { t } = useI18n('blog');
  const { locale } = useRouter();

  return (
    <Page hasScrollTop>
      <StyledBlog>
        <StyledBlogHeader>
          <Heading1 editorial>{t('blog.title')}</Heading1>
          <div
            dangerouslySetInnerHTML={{
              __html: t('blog.desc')
            }}
          ></div>
        </StyledBlogHeader>
        <StyledTags>
          {category && (
            <Link href={getAppUrl(APP_URLS.blog, locale)} passHref>
              <Badge as={'a'} variant={'secondary'}>
                {t('blog.all')}
              </Badge>
            </Link>
          )}

          {!category && (
            <Badge as={'span'} variant={'primary'}>
              {t('blog.all')}
            </Badge>
          )}

          {categories
            ?.filter(
              (c) =>
                c.slug !== 'uncategorised' &&
                c.slug !== 'oculto' &&
                c.slug !== 'sin-categorizar' &&
                c.slug !== 'all' &&
                c.slug !== 'todo-el-blog'
            )
            .map((tag) => (
              <Link
                key={tag.id}
                href={`${getAppUrl(APP_URLS.blog, locale)}/category/${
                  tag.slug
                }`}
                passHref
              >
                <Badge
                  as={'a'}
                  variant={
                    category && category === tag.slug ? 'primary' : 'secondary'
                  }
                >
                  {tag.name.charAt(0).toUpperCase() + tag.name.slice(1)}
                </Badge>
              </Link>
            ))}
        </StyledTags>
        <StyledBlogCards>
          {posts?.map((item) => {
            return (
              <BlogCard
                key={item.slug}
                title={item.title.rendered}
                url={item.slug}
                thumb={getPostThumb(item)}
                excerpt={item.excerpt.rendered}
                date={item.date}
              />
            );
          })}
        </StyledBlogCards>
        {totalPages > 1 && (
          <BlogPagination
            totalPosts={totalPosts}
            totalPages={totalPages}
            currentPage={currentPage}
            category={category}
          />
        )}
        <BlogCta />
      </StyledBlog>
    </Page>
  );
};

export default BlogComponent;
